import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Database, RefreshCwOff } from 'lucide-react';
import { fetchAnalytics } from '../../services/assets';
import ExportPoints from './ExportPoints';

const Analysis = () => {
  const { currentSiteId, userSites } = useAuth();
  const { theme } = useTheme();
  const [data, setData] = useState([]);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const response = await fetchAnalytics(currentSiteId);
        setData([...response].reverse());
        setSiteName(userSites.find(site => site.site_id === currentSiteId)?.name || '');
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentSiteId) {
      loadAnalytics();
    }
  }, [currentSiteId, userSites]);

  if (loading) {
    return (
      <div className="w-full min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="flex gap-2">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i}
                className="w-2 h-8 sm:w-3 sm:h-12 bg-blue-500 rounded-full animate-pulse"
                style={{
                  animation: `pulse 1s cubic-bezier(0.4, 0, 0.6, 1) ${i * 0.15}s infinite`,
                  opacity: 0.6 + (i * 0.2)
                }}
              />
            ))}
          </div>
          <div className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading Analytics
          </div>
        </div>
      </div>
    );
  }

  if (!data.length) return null;

  const latestData = data[0];
  const time = new Date(latestData.created_at).toLocaleTimeString();

  const STAGNANT_COLOR = '#eab308';
  const ACTIVE_COLOR = '#059669';
  const LOW_QUALITY_COLOR = '#be185d';
  const GOOD_COLOR = '#059669';

  const stagnantPieData = [
    {
      name: 'Stagnant',
      value: latestData.stagnant_points,
      percentage: ((latestData.stagnant_points / latestData.total_points) * 100).toFixed(1),
      fill: STAGNANT_COLOR
    },
    {
      name: 'Good',
      value: latestData.total_points - latestData.stagnant_points,
      percentage: (((latestData.total_points - latestData.stagnant_points) / latestData.total_points) * 100).toFixed(1),
      fill: ACTIVE_COLOR
    }
  ];

  const qualityPieData = [
    {
      name: 'Low Quality',
      value: latestData.low_quality_points,
      percentage: ((latestData.low_quality_points / latestData.total_points) * 100).toFixed(1),
      fill: LOW_QUALITY_COLOR
    },
    {
      name: 'Good',
      value: latestData.total_points - latestData.low_quality_points,
      percentage: (((latestData.total_points - latestData.low_quality_points) / latestData.total_points) * 100).toFixed(1),
      fill: GOOD_COLOR
    }
  ];

  const lineData = data.map(item => ({
    date: new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    Stagnant: (item.stagnant_points / item.total_points * 100).toFixed(1),
    Quality: (item.low_quality_points / item.total_points * 100).toFixed(1)
  })).reverse();

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={window.innerWidth < 640 ? 10 : 12}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="w-full h-[calc(100vh-64px)] p-2 sm:p-4 overflow-y-auto space-y-3 sm:space-y-6">
      <div className={`p-2 sm:p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <h2 className={`text-base sm:text-xl font-semibold mb-2 sm:mb-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
          Today's Statistics
        </h2>
        
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
            <div className={`p-2 sm:p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} flex items-center gap-2 shadow-sm`}>
              <Database className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 shrink-0" />
              <div className="min-w-0">
                <div className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Total Points</div>
                <div className="text-sm sm:text-lg font-bold text-blue-600 truncate">{latestData.total_points.toLocaleString()}</div>
              </div>
            </div>

            <div className={`p-2 sm:p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} flex items-center gap-2 shadow-sm`}>
              <RefreshCwOff className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 shrink-0" />
              <div className="min-w-0">
                <div className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Stagnant Points</div>
                <div className="text-sm sm:text-lg font-bold text-yellow-600 truncate">{latestData.stagnant_points.toLocaleString()}</div>
              </div>
            </div>

            <div className={`p-2 sm:p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} flex items-center gap-2 shadow-sm`}>
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600 shrink-0" />
              <div className="min-w-0">
                <div className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Low Quality Points</div>
                <div className="text-sm sm:text-lg font-bold text-pink-600 truncate">{latestData.low_quality_points.toLocaleString()}</div>
              </div>
            </div>

            <div className="sm:col-span-3 lg:col-span-1">
              <ExportPoints site_name={siteName} />
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            <div className={`h-[200px] sm:h-[350px] p-2 sm:p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
              <h4 className={`text-center text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Stagnant Data Distribution
              </h4>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={stagnantPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="80%" label={renderCustomizedLabel} labelLine={false} stroke="none" />
                  <Tooltip formatter={(value, name, props) => [`${props.payload.percentage}%`, name]} />
                  <Legend verticalAlign="bottom" height={36} iconSize={8} fontSize={10} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={`h-[200px] sm:h-[350px] p-2 sm:p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
              <h4 className={`text-center text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Data Quality Distribution
              </h4>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={qualityPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="80%" label={renderCustomizedLabel} labelLine={false} stroke="none" />
                  <Tooltip formatter={(value, name, props) => [`${props.payload.percentage}%`, name]} />
                  <Legend verticalAlign="bottom" height={36} iconSize={8} fontSize={10} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className={`p-2 sm:p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <h2 className={`text-base sm:text-xl font-semibold mb-2 sm:mb-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
          Trend - Last 30 Days
        </h2>
        
        <div className={`h-[250px] sm:h-[400px] p-2 sm:p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
          <ResponsiveContainer>
            <LineChart data={lineData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#374151' : '#E5E7EB'} />
              <XAxis dataKey="date" stroke={theme === 'dark' ? '#9CA3AF' : '#6B7280'} tick={{fontSize: window.innerWidth < 640 ? 10 : 12}} />
              <YAxis 
                stroke={theme === 'dark' ? '#9CA3AF' : '#6B7280'} 
                tickFormatter={(value) => `${value}%`} 
                tick={{fontSize: window.innerWidth < 640 ? 10 : 12}}
                domain={[0, Math.max(...lineData.map(item => Math.max(parseFloat(item.Stagnant), parseFloat(item.Quality))))]} // Set domain based on max value
              />
              <Tooltip contentStyle={{ backgroundColor: theme === 'dark' ? '#374151' : '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} formatter={(value) => [`${value}%`]} />
              <Legend wrapperStyle={{fontSize: window.innerWidth < 640 ? '10px' : '12px'}} />
              <Line type="monotone" dataKey="Stagnant" stroke="#eab308" strokeWidth={2} />
              <Line type="monotone" dataKey="Quality" stroke="#be185d" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analysis;
